/**
 * Audio alerts utility for critical map notifications
 */

// Audio context singleton
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch (e) {
      console.warn('Web Audio API not supported:', e);
      return null;
    }
  }
  return audioContext;
}

/**
 * Play a beep sound
 */
export function playBeep(frequency: number = 880, duration: number = 0.3, volume: number = 0.3): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    // Resume context if suspended (required for user gesture policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  } catch (e) {
    console.warn('Could not play beep:', e);
  }
}

/**
 * Play a double beep for warnings
 */
export function playWarningAlert(): void {
  playBeep(660, 0.2, 0.25);
  setTimeout(() => playBeep(660, 0.2, 0.25), 250);
}

/**
 * Play an urgent triple beep for critical alerts
 */
export function playCriticalAlert(): void {
  playBeep(880, 0.15, 0.3);
  setTimeout(() => playBeep(880, 0.15, 0.3), 200);
  setTimeout(() => playBeep(1100, 0.3, 0.3), 400);
}

/**
 * Play a success chime
 */
export function playSuccessChime(): void {
  playBeep(523, 0.1, 0.2); // C5
  setTimeout(() => playBeep(659, 0.1, 0.2), 100); // E5
  setTimeout(() => playBeep(784, 0.2, 0.2), 200); // G5
}

/**
 * Play an error sound
 */
export function playErrorSound(): void {
  playBeep(200, 0.3, 0.3);
  setTimeout(() => playBeep(150, 0.4, 0.3), 200);
}

/**
 * Play alert based on severity
 */
export function playAlertBySeverity(severity: 'info' | 'warning' | 'critical'): void {
  switch (severity) {
    case 'critical':
      playCriticalAlert();
      break;
    case 'warning':
      playWarningAlert();
      break;
    case 'info':
    default:
      playBeep(440, 0.2, 0.2);
      break;
  }
}

/**
 * Check if audio is available and can be played
 */
export function isAudioAvailable(): boolean {
  return !!(window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
}

/**
 * Resume audio context (call after user interaction)
 */
export async function resumeAudioContext(): Promise<void> {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    await ctx.resume();
  }
}

/**
 * Whether sound can actually play right now. Browsers keep an AudioContext
 * suspended until a user gesture (autoplay policy), so an alarm must know if it
 * is audible or only visual. Read-only: never creates the context, because a
 * context created outside a gesture starts suspended.
 */
export function isAudioContextRunning(): boolean {
  return audioContext?.state === 'running';
}
